import { useState, useEffect, useCallback, useRef } from 'react';
import { OptimizedQueries, type OptimizedVolunteer, type OptimizedEvent } from '@/utils/optimizedQueries';
import { supabase } from '@/lib/supabase';

// Web-specific wrapper: we only need useOptimizedVolunteers here, so we reimplement
// it using the shared OptimizedQueries utilities.

export function useOptimizedVolunteers(eventId?: string, organizationId?: string) {
  const [volunteers, setVolunteers] = useState<OptimizedVolunteer[]>([]);
  const [profilesUnavailable, setProfilesUnavailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eventIdRef = useRef(eventId);
  eventIdRef.current = eventId;

  const fetchVolunteers = useCallback(async () => {
    const requestedEventId = eventId;
    setVolunteers([]);
    setProfilesUnavailable(false);
    setLoading(true);
    setError(null);

    try {
      const result = await OptimizedQueries.performance.measureQuery(
        `getVolunteers-${eventId || 'all'}-${organizationId || 'all'}`,
        () => OptimizedQueries.volunteers.getVolunteers(eventId, organizationId),
      );
      if (requestedEventId !== eventIdRef.current) return;
      setVolunteers(result.volunteers);
      setProfilesUnavailable(result.profilesUnavailable);
    } catch (err) {
      if (requestedEventId !== eventIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to fetch volunteers');
    } finally {
      if (requestedEventId === eventIdRef.current) setLoading(false);
    }
  }, [eventId, organizationId]);

  useEffect(() => {
    fetchVolunteers();
  }, [fetchVolunteers]);

  const refetch = useCallback(async () => {
    if (eventId) {
      OptimizedQueries.cache.invalidateVolunteers(eventId);
    }
    await fetchVolunteers();
  }, [eventId, fetchVolunteers]);

  const updateVolunteerDepartment = useCallback(
    async (volunteerId: string, department: string | null) => {
      let snapshot: OptimizedVolunteer[] = [];
      const nextDepartments = department ? [department] : [];

      setVolunteers((current) => {
        snapshot = current;
        return current.map((v) =>
          v.id === volunteerId ? { ...v, departments: nextDepartments } : v,
        );
      });

      const previousVolunteer = snapshot.find((v) => v.id === volunteerId);
      const previousDepartment = previousVolunteer?.departments?.[0] ?? null;

      if (eventId) {
        OptimizedQueries.cache.patchVolunteerDepartment(eventId, volunteerId, department);
      }

      const revert = () => {
        setVolunteers(snapshot);
        if (eventId) {
          OptimizedQueries.cache.patchVolunteerDepartment(
            eventId,
            volunteerId,
            previousDepartment,
          );
        }
      };

      try {
        const { data, error } = await supabase
          .from('volunteers')
          .update({ department })
          .eq('id', volunteerId)
          .select('id')
          .maybeSingle();

        if (error) {
          throw new Error(error.message || 'Failed to update department');
        }
        if (!data) {
          throw new Error('Could not update department. You may not have permission.');
        }
      } catch (err) {
        revert();
        throw err instanceof Error ? err : new Error('Failed to update department');
      }
    },
    [eventId],
  );

  return {
    volunteers,
    profilesUnavailable,
    loading,
    error,
    refetch,
    updateVolunteerDepartment,
  };
}

export function useOptimizedEvents(organizationId?: string, isPublic?: boolean, limit: number = 50) {
  const [events, setEvents] = useState<OptimizedEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(
    async (forceRefresh: boolean = false) => {
      setLoading(true);
      setError(null);

      try {
        const result = await OptimizedQueries.performance.measureQuery(
          `getEvents-${organizationId || 'all'}-${isPublic}-${limit}`,
          () => OptimizedQueries.events.getEvents(organizationId, isPublic, limit, forceRefresh),
        );
        setEvents(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch events');
      } finally {
        setLoading(false);
      }
    },
    [organizationId, isPublic, limit],
  );

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return {
    events,
    loading,
    error,
    refetch: fetchEvents,
    forceRefresh: () => fetchEvents(true),
  };
}

export type { OptimizedVolunteer, OptimizedEvent };




